import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  Save,
  ArrowLeft,
  Users,
  MessageSquare,
  Briefcase,
  Download,
  Plus,
  Pencil,
  Trash2,
  ChevronLeft,
  ChevronRight,
  X,
  Check,
  Upload,
  BarChart3,
} from 'lucide-react';
import { companyApi, jobPostingApi, jobTypeApi, JobPosting } from '../../services/api';
import AdminHeader from '../common/AdminHeader';
import UserManagement from './UserManagement';
import UsageStatsTab from './UsageStatsTab';

// 固定質問の型定義
interface FixedQuestion {
  question: string;
  instruction: string;
}

interface CompanySettingsProps {
  companyId: string;
  companyName: string;
}

type SettingsTab = 'basic' | 'jobs' | 'usage' | 'users';

const ITEMS_PER_PAGE = 10;

const getPageNumbers = (current: number, total: number): (number | '...')[] => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= 3) return [1, 2, 3, 4, '...', total];
  if (current >= total - 2) return [1, '...', total - 3, total - 2, total - 1, total];
  return [1, '...', current - 1, current, current + 1, '...', total];
};

const CompanySettings: React.FC<CompanySettingsProps> = ({ companyId, companyName }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<SettingsTab>('basic');
  const [editedCompanyName, setEditedCompanyName] = useState(companyName);
  const [currentIndustryName, setCurrentIndustryName] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const [loadingCustom, setLoadingCustom] = useState<boolean>(false);
  const [skill1Title, setSkill1Title] = useState<string>('');
  const [skill1Prompt, setSkill1Prompt] = useState<string>('');
  const [skill2Title, setSkill2Title] = useState<string>('');
  const [skill2Prompt, setSkill2Prompt] = useState<string>('');
  const [skill3Title, setSkill3Title] = useState<string>('');
  const [skill3Prompt, setSkill3Prompt] = useState<string>('');

  // 職種設定の状態
  const [loadingJobTypes, setLoadingJobTypes] = useState<boolean>(false);
  const [allJobTypes, setAllJobTypes] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedJobTypeIds, setSelectedJobTypeIds] = useState<Set<string>>(new Set());

  // 固定質問の状態（最大5個）
  const [loadingFixedQuestions, setLoadingFixedQuestions] = useState<boolean>(false);
  const [fixedQuestions, setFixedQuestions] = useState<FixedQuestion[]>([
    { question: '', instruction: '' },
    { question: '', instruction: '' },
    { question: '', instruction: '' },
    { question: '', instruction: '' },
    { question: '', instruction: '' },
  ]);

  // 応募求人マスタの状態
  const [jobPostings, setJobPostings] = useState<JobPosting[]>([]);
  const [jobPostingLoading, setJobPostingLoading] = useState<boolean>(false);
  const [jobPostingError, setJobPostingError] = useState<string | null>(null);
  const [jobPostingDrafts, setJobPostingDrafts] = useState<
    Record<string, { title: string; description: string; sortOrder: number | null }>
  >({});
  const [newJobPostingTitle, setNewJobPostingTitle] = useState('');
  const [newJobPostingDescription, setNewJobPostingDescription] = useState('');
  const [jobPostingSavingId, setJobPostingSavingId] = useState<string | null>(null);
  const [jobPostingImportFile, setJobPostingImportFile] = useState<File | null>(null);
  const [jobPostingImportMessage, setJobPostingImportMessage] = useState<string | null>(null);
  const [jobPostingToDelete, setJobPostingToDelete] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editingJobPostingId, setEditingJobPostingId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showNewJobPostingForm, setShowNewJobPostingForm] = useState(false);

  const handleGoBack = () => {
    navigate(`/admin/${companyId}`);
  };

  const handleSave = async () => {
    if (editedCompanyName.trim() === '') {
      setSaveMessage({ type: 'error', text: '会社名は必須です' });
      return;
    }

    try {
      setIsSaving(true);
      setSaveMessage(null);

      const response = await companyApi.updateCompany(companyId, {
        name: editedCompanyName.trim(),
      });

      if (response.success) {
        setSaveMessage({ type: 'success', text: '基本情報を更新しました' });
        // 3秒後にメッセージを消す
        setTimeout(() => setSaveMessage(null), 3000);
      } else {
        setSaveMessage({ type: 'error', text: '更新に失敗しました' });
      }
    } catch (error) {
      console.error('Company update error:', error);
      setSaveMessage({ type: 'error', text: '更新中にエラーが発生しました' });
    } finally {
      setIsSaving(false);
    }
  };

  // 企業の現在の業界名を読み込み
  useEffect(() => {
    const loadCompanyIndustry = async () => {
      try {
        const companyRes = await companyApi.getCompany(companyId);
        if (companyRes.success && companyRes.data?.industry?.name) {
          setCurrentIndustryName(companyRes.data.industry.name);
        }
      } catch (e) {
        console.error('Company industry load error:', e);
      }
    };
    loadCompanyIndustry();
  }, [companyId]);

  // カスタムスキルの読み込み
  useEffect(() => {
    const load = async () => {
      try {
        setLoadingCustom(true);
        const res = await companyApi.getCompanyCustomSkills(companyId);
        if (res.success && res.data) {
          const data = res.data as any;
          const cs = data.custom_skills || data.customSkills || {};
          const s1 = cs.skill_1 || {};
          const s2 = cs.skill_2 || {};
          const s3 = cs.skill_3 || {};
          setSkill1Title(typeof s1.title === 'string' ? s1.title : '');
          setSkill1Prompt(typeof s1.prompt === 'string' ? s1.prompt : '');
          setSkill2Title(typeof s2.title === 'string' ? s2.title : '');
          setSkill2Prompt(typeof s2.prompt === 'string' ? s2.prompt : '');
          setSkill3Title(typeof s3.title === 'string' ? s3.title : '');
          setSkill3Prompt(typeof s3.prompt === 'string' ? s3.prompt : '');
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingCustom(false);
      }
    };
    load();
  }, [companyId]);

  // 固定質問の読み込み
  useEffect(() => {
    const loadFixedQuestions = async () => {
      try {
        setLoadingFixedQuestions(true);
        const res = await companyApi.getCompanyFixedQuestions(companyId);
        if (res.success && res.data?.questions) {
          const loadedQuestions = res.data.questions;
          const newQuestions: FixedQuestion[] = Array(5)
            .fill(null)
            .map((_, index) => {
              const q = loadedQuestions.find((lq) => lq.questionNumber === index + 1);
              return {
                question: q?.question || '',
                instruction: q?.instruction || '',
              };
            });
          setFixedQuestions(newQuestions);
        }
      } catch (e) {
        console.error('Fixed questions load error:', e);
      } finally {
        setLoadingFixedQuestions(false);
      }
    };
    loadFixedQuestions();
  }, [companyId]);

  // 応募求人マスタの読み込み
  useEffect(() => {
    const loadJobPostings = async () => {
      try {
        setJobPostingLoading(true);
        setJobPostingError(null);
        const res = await jobPostingApi.getJobPostings(companyId);
        if (res.success && res.data) {
          // sortOrderでソート
          const sortedPostings = [...res.data].sort((a, b) => {
            if (a.sortOrder === null && b.sortOrder === null) return 0;
            if (a.sortOrder === null) return 1;
            if (b.sortOrder === null) return -1;
            return a.sortOrder - b.sortOrder;
          });
          setJobPostings(sortedPostings);
          const drafts = sortedPostings.reduce(
            (acc, posting) => ({
              ...acc,
              [posting.id]: {
                title: posting.title || '',
                description: posting.description || '',
                sortOrder: posting.sortOrder ?? null,
              },
            }),
            {} as Record<string, { title: string; description: string; sortOrder: number | null }>
          );
          setJobPostingDrafts(drafts);
        } else {
          setJobPostingError(res.message || '応募求人一覧の取得に失敗しました');
        }
      } catch (e) {
        console.error('Job posting load error:', e);
        setJobPostingError('応募求人一覧の取得中にエラーが発生しました');
      } finally {
        setJobPostingLoading(false);
      }
    };
    loadJobPostings();
  }, [companyId]);

  // 職種設定の読み込み
  useEffect(() => {
    const loadJobTypes = async () => {
      try {
        setLoadingJobTypes(true);
        const [allRes, selectedRes] = await Promise.all([
          jobTypeApi.getJobTypes(),
          companyApi.getCompanySelectedJobTypes(companyId),
        ]);

        if (allRes.success && allRes.data) {
          setAllJobTypes(allRes.data);
        }
        if (selectedRes.success && selectedRes.data?.selectedJobTypes) {
          setSelectedJobTypeIds(new Set(selectedRes.data.selectedJobTypes.map((s) => s.jobTypeId)));
        }
      } catch (e) {
        console.error('Job types load error:', e);
      } finally {
        setLoadingJobTypes(false);
      }
    };
    loadJobTypes();
  }, [companyId]);

  // 職種チェックボックスの変更ハンドラ
  const handleJobTypeToggle = (jobTypeId: string) => {
    setSelectedJobTypeIds((prev) => {
      const next = new Set(prev);
      if (next.has(jobTypeId)) {
        next.delete(jobTypeId);
      } else {
        next.add(jobTypeId);
      }
      return next;
    });
  };

  // 職種設定の保存ハンドラ
  const handleSaveJobTypes = async () => {
    try {
      setIsSaving(true);
      setSaveMessage(null);

      const response = await companyApi.updateCompanySelectedJobTypes(
        companyId,
        Array.from(selectedJobTypeIds)
      );
      if (response.success) {
        setSaveMessage({ type: 'success', text: '職種設定を更新しました' });
        setTimeout(() => setSaveMessage(null), 3000);
      } else {
        setSaveMessage({ type: 'error', text: response.message || '更新に失敗しました' });
      }
    } catch (error) {
      console.error('Job type selection update error:', error);
      setSaveMessage({ type: 'error', text: '職種設定の更新中にエラーが発生しました' });
    } finally {
      setIsSaving(false);
    }
  };

  // 固定質問の更新ハンドラ
  const handleFixedQuestionChange = (index: number, field: keyof FixedQuestion, value: string) => {
    setFixedQuestions((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // 固定質問のクリアハンドラ
  const handleClearFixedQuestion = (index: number) => {
    setFixedQuestions((prev) => {
      const updated = [...prev];
      updated[index] = { question: '', instruction: '' };
      return updated;
    });
  };

  // 固定質問の保存ハンドラ
  const handleSaveFixedQuestions = async () => {
    try {
      setIsSaving(true);
      setSaveMessage(null);

      // 空でない質問のみを抽出
      const questionsToSave = fixedQuestions
        .filter((q) => q.question.trim() !== '')
        .map((q) => ({
          question: q.question.trim(),
          instruction: q.instruction.trim() || undefined,
        }));

      const response = await companyApi.updateCompanyFixedQuestions(companyId, questionsToSave);
      if (response.success) {
        setSaveMessage({ type: 'success', text: '固定質問を更新しました' });
        setTimeout(() => setSaveMessage(null), 3000);
      } else {
        setSaveMessage({ type: 'error', text: response.message || '更新に失敗しました' });
      }
    } catch (error) {
      console.error('Fixed questions update error:', error);
      setSaveMessage({ type: 'error', text: '固定質問の更新中にエラーが発生しました' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleJobPostingDraftChange = (
    id: string,
    field: 'title' | 'description' | 'sortOrder',
    value: string | number | null
  ) => {
    setJobPostingDrafts((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: field === 'sortOrder' ? (value === '' ? null : Number(value)) : value,
      },
    }));
  };

  const handleCreateJobPosting = async () => {
    const title = newJobPostingTitle.trim();
    const description = newJobPostingDescription.trim();
    if (!title) {
      setJobPostingError('求人タイトルは必須です');
      return;
    }
    try {
      setJobPostingSavingId('new');
      setJobPostingError(null);
      const res = await jobPostingApi.createJobPosting(companyId, {
        title,
        description: description || null,
      });
      if (res.success && res.data) {
        setJobPostings((prev) => [res.data as JobPosting, ...prev]);
        setJobPostingDrafts((prev) => ({
          ...prev,
          [res.data!.id]: {
            title: res.data!.title || '',
            description: res.data!.description || '',
            sortOrder: res.data!.sortOrder ?? null,
          },
        }));
        setNewJobPostingTitle('');
        setNewJobPostingDescription('');
        setShowNewJobPostingForm(false);
        setCurrentPage(1);
      } else {
        setJobPostingError(res.message || '応募求人の作成に失敗しました');
      }
    } catch (e) {
      console.error('Create job posting error:', e);
      setJobPostingError('応募求人の作成中にエラーが発生しました');
    } finally {
      setJobPostingSavingId(null);
    }
  };

  const handleUpdateJobPosting = async (id: string) => {
    const draft = jobPostingDrafts[id];
    if (!draft) return;
    const title = draft.title.trim();
    if (!title) {
      setJobPostingError('求人タイトルは必須です');
      return;
    }
    try {
      setJobPostingSavingId(id);
      setJobPostingError(null);
      const res = await jobPostingApi.updateJobPosting(companyId, id, {
        title,
        description: draft.description.trim() || null,
        sortOrder: draft.sortOrder,
      });
      if (res.success && res.data) {
        const updated = jobPostings.map((p) => (p.id === id ? (res.data as JobPosting) : p));
        // sortOrderでソート
        const sorted = [...updated].sort((a, b) => {
          if (a.sortOrder === null && b.sortOrder === null) return 0;
          if (a.sortOrder === null) return 1;
          if (b.sortOrder === null) return -1;
          return a.sortOrder - b.sortOrder;
        });
        setJobPostings(sorted);
        // draftsも更新
        setJobPostingDrafts((prev) => ({
          ...prev,
          [id]: {
            title: res.data!.title || '',
            description: res.data!.description || '',
            sortOrder: res.data!.sortOrder ?? null,
          },
        }));
        setEditingJobPostingId(null);
      } else {
        setJobPostingError(res.message || '応募求人の更新に失敗しました');
      }
    } catch (e) {
      console.error('Update job posting error:', e);
      setJobPostingError('応募求人の更新中にエラーが発生しました');
    } finally {
      setJobPostingSavingId(null);
    }
  };

  const handleDeleteJobPostingClick = (id: string) => {
    setJobPostingToDelete(id);
    setShowDeleteConfirm(true);
  };

  const handleDeleteJobPostingConfirm = async () => {
    if (!jobPostingToDelete) return;

    try {
      setJobPostingSavingId(jobPostingToDelete);
      setJobPostingError(null);
      const res = await jobPostingApi.deleteJobPosting(companyId, jobPostingToDelete);
      if (res.success) {
        const remainingCount = jobPostings.length - 1;
        const maxPage = Math.max(1, Math.ceil(remainingCount / ITEMS_PER_PAGE));
        if (currentPage > maxPage) {
          setCurrentPage(maxPage);
        }
        setJobPostings((prev) => prev.filter((p) => p.id !== jobPostingToDelete));
        setJobPostingDrafts((prev) => {
          const next = { ...prev };
          delete next[jobPostingToDelete];
          return next;
        });
        // 使用中だった場合の警告メッセージを表示
        if (res.data?.wasInUse) {
          setJobPostingError(
            `応募求人を削除しました（${res.data.usageCount}件の面接で使用されていました）`
          );
          setTimeout(() => setJobPostingError(null), 5000);
        }
      } else {
        setJobPostingError(res.message || '応募求人の削除に失敗しました');
      }
    } catch (e) {
      console.error('Delete job posting error:', e);
      setJobPostingError('応募求人の削除中にエラーが発生しました');
    } finally {
      setJobPostingSavingId(null);
      setJobPostingToDelete(null);
      setShowDeleteConfirm(false);
    }
  };

  const handleDeleteJobPostingCancel = () => {
    setJobPostingToDelete(null);
    setShowDeleteConfirm(false);
  };

  const handleCancelEdit = (id: string) => {
    const original = jobPostings.find((p) => p.id === id);
    if (original) {
      setJobPostingDrafts((prev) => ({
        ...prev,
        [id]: {
          title: original.title || '',
          description: original.description || '',
          sortOrder: original.sortOrder ?? null,
        },
      }));
    }
    setEditingJobPostingId(null);
  };

  const handleDownloadTemplate = async () => {
    try {
      await jobPostingApi.downloadJobPostingTemplate(companyId);
    } catch (e) {
      console.error('Download template error:', e);
      setJobPostingError('CSVテンプレートのダウンロード中にエラーが発生しました');
    }
  };

  const handleImportJobPostings = async () => {
    if (!jobPostingImportFile) {
      setJobPostingImportMessage('CSVファイルを選択してください');
      return;
    }
    try {
      setJobPostingSavingId('import');
      setJobPostingImportMessage(null);
      const res = await jobPostingApi.importJobPostings(companyId, jobPostingImportFile);
      if (res.success && res.data) {
        setJobPostingImportMessage(
          `インポート完了: ${res.data.imported}件（エラー ${res.data.errors.length}件）`
        );
        const refreshed = await jobPostingApi.getJobPostings(companyId);
        if (refreshed.success && refreshed.data) {
          const sortedPostings = [...refreshed.data].sort((a, b) => {
            if (a.sortOrder === null && b.sortOrder === null) return 0;
            if (a.sortOrder === null) return 1;
            if (b.sortOrder === null) return -1;
            return a.sortOrder - b.sortOrder;
          });
          setJobPostings(sortedPostings);
          const drafts = sortedPostings.reduce(
            (acc, posting) => ({
              ...acc,
              [posting.id]: {
                title: posting.title || '',
                description: posting.description || '',
                sortOrder: posting.sortOrder ?? null,
              },
            }),
            {} as Record<string, { title: string; description: string; sortOrder: number | null }>
          );
          setJobPostingDrafts(drafts);
        }
      } else {
        setJobPostingImportMessage(res.message || 'CSVインポートに失敗しました');
      }
    } catch (e) {
      console.error('Import job postings error:', e);
      setJobPostingImportMessage('CSVインポート中にエラーが発生しました');
    } finally {
      setJobPostingSavingId(null);
    }
  };

  const handleSaveCustom = async () => {
    try {
      setIsSaving(true);
      setSaveMessage(null);

      // 入力バリデーション: prompt がある場合、title は必須
      const t1 = skill1Title.trim();
      const p1 = skill1Prompt.trim();
      const t2 = skill2Title.trim();
      const p2 = skill2Prompt.trim();
      const t3 = skill3Title.trim();
      const p3 = skill3Prompt.trim();

      if (p1 && !t1) {
        setSaveMessage({ type: 'error', text: '追加評価項目 1 のタイトルは必須です' });
        setIsSaving(false);
        return;
      }
      if (p2 && !t2) {
        setSaveMessage({ type: 'error', text: '追加評価項目 2 のタイトルは必須です' });
        setIsSaving(false);
        return;
      }
      if (p3 && !t3) {
        setSaveMessage({ type: 'error', text: '追加評価項目 3 のタイトルは必須です' });
        setIsSaving(false);
        return;
      }

      const skills: Record<string, any> = {};
      if (t1 || p1) {
        skills.skill_1 = { title: t1, ...(p1 ? { prompt: p1 } : {}) };
      }
      if (t2 || p2) {
        skills.skill_2 = { title: t2, ...(p2 ? { prompt: p2 } : {}) };
      }
      if (t3 || p3) {
        skills.skill_3 = { title: t3, ...(p3 ? { prompt: p3 } : {}) };
      }

      const payload =
        Object.keys(skills).length > 0 ? { custom_skills: skills } : { custom_skills: {} };

      const response = await companyApi.updateCompanyCustomSkills(companyId, payload);
      if (response.success) {
        setSaveMessage({ type: 'success', text: 'カスタムスキル設定を更新しました' });
        setTimeout(() => setSaveMessage(null), 3000);
      } else {
        setSaveMessage({ type: 'error', text: response.message || '更新に失敗しました' });
      }
    } catch (error) {
      console.error('Custom skills update error:', error);
      setSaveMessage({ type: 'error', text: 'JSONが不正か、更新中にエラーが発生しました' });
    } finally {
      setIsSaving(false);
    }
  };

  // 入力欄用のインラインエラーメッセージ（promptがあるのにtitleが空）
  const title1Error =
    skill1Prompt.trim().length > 0 && skill1Title.trim().length === 0 ? 'タイトルは必須です' : '';
  const title2Error =
    skill2Prompt.trim().length > 0 && skill2Title.trim().length === 0 ? 'タイトルは必須です' : '';
  const title3Error =
    skill3Prompt.trim().length > 0 && skill3Title.trim().length === 0 ? 'タイトルは必須です' : '';
  const hasInlineError = Boolean(title1Error || title2Error || title3Error);

  // ページネーション計算
  const totalPages = Math.max(1, Math.ceil(jobPostings.length / ITEMS_PER_PAGE));
  const paginatedJobPostings = jobPostings.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ヘッダー */}
      <AdminHeader
        companyId={companyId}
        companyName={companyName}
        title="組織設定"
        subtitle="組織の基本情報を管理します"
        currentPage="settings"
      />

      {/* メインコンテンツ */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="space-y-8">
          {/* 戻るボタン */}
          <div>
            <button
              onClick={handleGoBack}
              className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              戻る
            </button>
          </div>

          {/* タブナビゲーション */}
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8" aria-label="設定タブ">
              <button
                onClick={() => setActiveTab('basic')}
                className={`flex items-center gap-2 py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                  activeTab === 'basic'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Building2 className="w-4 h-4" />
                基本設定
              </button>
              <button
                onClick={() => setActiveTab('jobs')}
                className={`flex items-center gap-2 py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                  activeTab === 'jobs'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Briefcase className="w-4 h-4" />
                求人管理
              </button>
              <button
                onClick={() => setActiveTab('users')}
                className={`flex items-center gap-2 py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                  activeTab === 'users'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Users className="w-4 h-4" />
                ユーザー管理
              </button>
              <button
                onClick={() => setActiveTab('usage')}
                className={`flex items-center gap-2 py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                  activeTab === 'usage'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <BarChart3 className="w-4 h-4" />
                利用状況
              </button>
            </nav>
          </div>

          {/* 保存メッセージ */}
          {saveMessage && (
            <div
              className={`p-4 rounded-md ${
                saveMessage.type === 'success'
                  ? 'bg-green-50 text-green-800 border border-green-200'
                  : 'bg-red-50 text-red-800 border border-red-200'
              }`}
            >
              {saveMessage.text}
            </div>
          )}

          {/* 基本設定タブ */}
          {activeTab === 'basic' && (
            <div className="space-y-8">
              {/* 基本情報 */}
              <div className="bg-white rounded-lg shadow-sm border">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center space-x-3">
                    <Building2 className="w-5 h-5 text-gray-600" />
                    <h2 className="text-lg font-semibold text-gray-900">基本情報</h2>
                  </div>
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      組織名 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={editedCompanyName}
                      onChange={(e) => setEditedCompanyName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="組織名を入力してください"
                      disabled={isSaving}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">業界</label>
                    <input
                      type="text"
                      value={currentIndustryName || '未設定'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
                      readOnly
                      disabled
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      業界の変更は管理者にお問い合わせください
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">組織ID</label>
                    <input
                      type="text"
                      value={companyId}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
                      readOnly
                    />
                    <p className="mt-1 text-xs text-gray-500">組織IDは変更できません</p>
                  </div>
                  <div className="flex justify-end">
                    <button
                      onClick={handleSave}
                      disabled={isSaving}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                      {isSaving ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>保存中...</span>
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          <span>基本情報を保存</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* カスタムスキル設定 */}
              <div className="bg-white rounded-lg shadow-sm border">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h3 className="text-md font-semibold text-gray-900">カスタムスキル設定</h3>
                    {loadingCustom && (
                      <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
                    )}
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    組織ごとの追加分析項目（custom_skills）を定義します。title と prompt
                    を最大3つまで入力できます（空でも可）。
                  </p>
                </div>
                <div className="p-6 space-y-6">
                  {/* Skill 1 */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-gray-900">追加評価項目 1</h4>
                      {(skill1Title || skill1Prompt) && (
                        <button
                          onClick={() => {
                            setSkill1Title('');
                            setSkill1Prompt('');
                          }}
                          className="text-xs text-gray-500 hover:text-gray-700"
                        >
                          クリア
                        </button>
                      )}
                    </div>
                    <input
                      type="text"
                      value={skill1Title}
                      onChange={(e) => setSkill1Title(e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${title1Error ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-transparent'}`}
                      placeholder="タイトル（例: ドメイン専門性）"
                      aria-invalid={Boolean(title1Error)}
                    />
                    {title1Error && <p className="text-xs text-red-600">{title1Error}</p>}
                    <textarea
                      value={skill1Prompt}
                      onChange={(e) => setSkill1Prompt(e.target.value)}
                      className="w-full h-24 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="補足プロンプト（任意）"
                    />
                  </div>

                  {/* Skill 2 */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-gray-900">追加評価項目 2</h4>
                      {(skill2Title || skill2Prompt) && (
                        <button
                          onClick={() => {
                            setSkill2Title('');
                            setSkill2Prompt('');
                          }}
                          className="text-xs text-gray-500 hover:text-gray-700"
                        >
                          クリア
                        </button>
                      )}
                    </div>
                    <input
                      type="text"
                      value={skill2Title}
                      onChange={(e) => setSkill2Title(e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${title2Error ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-transparent'}`}
                      placeholder="タイトル（例: リスク感度）"
                      aria-invalid={Boolean(title2Error)}
                    />
                    {title2Error && <p className="text-xs text-red-600">{title2Error}</p>}
                    <textarea
                      value={skill2Prompt}
                      onChange={(e) => setSkill2Prompt(e.target.value)}
                      className="w-full h-24 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="補足プロンプト（任意）"
                    />
                  </div>

                  {/* Skill 3 */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-gray-900">追加評価項目 3</h4>
                      {(skill3Title || skill3Prompt) && (
                        <button
                          onClick={() => {
                            setSkill3Title('');
                            setSkill3Prompt('');
                          }}
                          className="text-xs text-gray-500 hover:text-gray-700"
                        >
                          クリア
                        </button>
                      )}
                    </div>
                    <input
                      type="text"
                      value={skill3Title}
                      onChange={(e) => setSkill3Title(e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${title3Error ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-transparent'}`}
                      placeholder="タイトル（例: リモート適性）"
                      aria-invalid={Boolean(title3Error)}
                    />
                    {title3Error && <p className="text-xs text-red-600">{title3Error}</p>}
                    <textarea
                      value={skill3Prompt}
                      onChange={(e) => setSkill3Prompt(e.target.value)}
                      className="w-full h-24 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="補足プロンプト（任意）"
                    />
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={handleSaveCustom}
                      disabled={isSaving || hasInlineError}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                      {isSaving ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>保存中...</span>
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          <span>カスタムスキルを保存</span>
                        </>
                      )}
                    </button>
                  </div>
                  <div className="text-xs text-gray-500 space-y-1">
                    <p>この定義に基づき評価エージェントが追加分析を実施します。</p>
                  </div>
                </div>
              </div>

              {/* 固定質問設定 */}
              <div className="bg-white rounded-lg shadow-sm border">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <MessageSquare className="w-5 h-5 text-green-600" />
                      <h3 className="text-md font-semibold text-gray-900">固定質問設定</h3>
                    </div>
                    {loadingFixedQuestions && (
                      <div className="w-4 h-4 border-2 border-gray-300 border-t-green-600 rounded-full animate-spin" />
                    )}
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    面接中に候補者へ必ず聞きたい質問を最大5つまで設定できます。
                    面接エージェントが適切なタイミングでこれらの質問を行います。
                  </p>
                </div>
                <div className="p-6 space-y-6">
                  {fixedQuestions.map((question, index) => (
                    <div key={index} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-gray-900">固定質問 {index + 1}</h4>
                        {(question.question || question.instruction) && (
                          <button
                            onClick={() => handleClearFixedQuestion(index)}
                            className="text-xs text-gray-500 hover:text-gray-700"
                          >
                            クリア
                          </button>
                        )}
                      </div>
                      <input
                        type="text"
                        value={question.question}
                        onChange={(e) =>
                          handleFixedQuestionChange(index, 'question', e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder={`質問文（例: ${index === 0 ? '今回の転職で叶えたいことBEST3' : index === 1 ? 'キャリアプラン' : index === 2 ? '価値観・カルチャー面で大事にしたいこと' : index === 3 ? '働き方・チームスタイルの嗜好性' : '追加質問'}）`}
                      />
                      <textarea
                        value={question.instruction}
                        onChange={(e) =>
                          handleFixedQuestionChange(index, 'instruction', e.target.value)
                        }
                        className="w-full h-16 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="補足指示（任意）例: 具体的に3つ挙げてもらうよう促す"
                      />
                    </div>
                  ))}

                  <div className="flex justify-end">
                    <button
                      onClick={handleSaveFixedQuestions}
                      disabled={isSaving}
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                      {isSaving ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>保存中...</span>
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          <span>固定質問を保存</span>
                        </>
                      )}
                    </button>
                  </div>
                  <div className="text-xs text-gray-500 space-y-1">
                    <p>
                      設定した質問は面接エージェントが適切なタイミングで候補者に質問し、評価レポートに回答が記録されます。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 求人管理タブ */}
          {activeTab === 'jobs' && (
            <div className="space-y-8">
              {/* 応募求人マスタセクション */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                {/* ヘッダー */}
                <div className="px-6 py-5 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-indigo-50 rounded-lg">
                        <Briefcase className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">応募求人マスタ</h3>
                        <p className="text-sm text-gray-500 mt-0.5">
                          応募求人のタイトルと説明を登録します。面接設定時にプルダウンで選択できます。
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {jobPostingLoading && (
                        <div className="w-5 h-5 border-2 border-gray-300 border-t-indigo-600 rounded-full animate-spin" />
                      )}
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700">
                        {jobPostings.length}件登録済み
                      </span>
                    </div>
                  </div>
                </div>

                {/* エラーメッセージ */}
                {jobPostingError && (
                  <div className="mx-6 mt-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200 flex items-start gap-2">
                    <span className="shrink-0 mt-0.5">⚠</span>
                    <span>{jobPostingError}</span>
                  </div>
                )}

                {/* アクションバー */}
                <div className="px-6 py-4 flex flex-wrap gap-3 items-center justify-between border-b border-gray-100 bg-gray-50/50">
                  <button
                    onClick={() => setShowNewJobPostingForm(!showNewJobPostingForm)}
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all text-sm font-medium shadow-sm hover:shadow"
                  >
                    <Plus className="w-4 h-4" />
                    新規求人を追加
                  </button>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleDownloadTemplate}
                      className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-white hover:text-gray-900 transition-colors text-sm"
                    >
                      <Download className="w-3.5 h-3.5" />
                      テンプレートDL
                    </button>
                    <input
                      type="file"
                      accept=".csv"
                      id="csv-import-input"
                      onChange={(e) => setJobPostingImportFile(e.target.files?.[0] || null)}
                      className="hidden"
                    />
                    <label
                      htmlFor="csv-import-input"
                      className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-white hover:text-gray-900 transition-colors text-sm cursor-pointer"
                    >
                      <Upload className="w-3.5 h-3.5" />
                      {jobPostingImportFile ? jobPostingImportFile.name : 'CSV選択'}
                    </label>
                    {jobPostingImportFile && (
                      <button
                        onClick={handleImportJobPostings}
                        disabled={jobPostingSavingId === 'import'}
                        className="inline-flex items-center gap-1.5 px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm disabled:opacity-50"
                      >
                        {jobPostingSavingId === 'import' ? '取り込み中...' : 'インポート実行'}
                      </button>
                    )}
                  </div>
                </div>

                {/* CSVインポートメッセージ */}
                {jobPostingImportMessage && (
                  <div className="mx-6 mt-3 p-2.5 rounded-lg bg-blue-50 text-blue-700 text-xs border border-blue-200">
                    {jobPostingImportMessage}
                  </div>
                )}

                {/* 新規追加フォーム（折りたたみ） */}
                {showNewJobPostingForm && (
                  <div className="mx-6 mt-4 p-5 bg-indigo-50/50 border border-indigo-200 rounded-xl space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                        <Plus className="w-4 h-4 text-indigo-600" />
                        新規求人の追加
                      </h4>
                      <button
                        onClick={() => setShowNewJobPostingForm(false)}
                        className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-white transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="sm:col-span-1">
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          タイトル <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={newJobPostingTitle}
                          onChange={(e) => setNewJobPostingTitle(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm bg-white"
                          placeholder="求人タイトルを入力"
                          disabled={jobPostingSavingId === 'new'}
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">説明</label>
                        <input
                          type="text"
                          value={newJobPostingDescription}
                          onChange={(e) => setNewJobPostingDescription(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm bg-white"
                          placeholder="求人説明（任意）"
                          disabled={jobPostingSavingId === 'new'}
                        />
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <button
                        onClick={handleCreateJobPosting}
                        disabled={jobPostingSavingId === 'new' || !newJobPostingTitle.trim()}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {jobPostingSavingId === 'new' ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            追加中...
                          </>
                        ) : (
                          <>
                            <Plus className="w-4 h-4" />
                            追加
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {/* テーブル */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50/80">
                        <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[200px]">
                          タイトル
                        </th>
                        <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          説明
                        </th>
                        <th className="px-6 py-3.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider w-36">
                          アクション
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {!jobPostingLoading && paginatedJobPostings.length === 0 && (
                        <tr>
                          <td colSpan={3} className="px-6 py-16 text-center">
                            <div className="flex flex-col items-center gap-3">
                              <div className="p-3 bg-gray-100 rounded-full">
                                <Briefcase className="w-6 h-6 text-gray-400" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-600">
                                  応募求人が未登録です
                                </p>
                                <p className="text-xs text-gray-400 mt-1">
                                  「新規求人を追加」ボタンから追加してください
                                </p>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                      {paginatedJobPostings.map((posting) => (
                        <tr
                          key={posting.id}
                          className={`group transition-colors ${
                            editingJobPostingId === posting.id
                              ? 'bg-indigo-50/40'
                              : 'hover:bg-gray-50/80'
                          }`}
                        >
                          {editingJobPostingId === posting.id ? (
                            <>
                              <td className="px-6 py-3">
                                <input
                                  type="text"
                                  value={jobPostingDrafts[posting.id]?.title || ''}
                                  onChange={(e) =>
                                    handleJobPostingDraftChange(posting.id, 'title', e.target.value)
                                  }
                                  className="w-full px-2.5 py-1.5 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                                />
                              </td>
                              <td className="px-6 py-3">
                                <input
                                  type="text"
                                  value={jobPostingDrafts[posting.id]?.description || ''}
                                  onChange={(e) =>
                                    handleJobPostingDraftChange(
                                      posting.id,
                                      'description',
                                      e.target.value
                                    )
                                  }
                                  className="w-full px-2.5 py-1.5 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                                  placeholder="説明（任意）"
                                />
                              </td>
                              <td className="px-6 py-3">
                                <div className="flex items-center justify-end gap-1.5">
                                  <button
                                    onClick={() => handleUpdateJobPosting(posting.id)}
                                    disabled={jobPostingSavingId === posting.id}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50"
                                    title="保存"
                                  >
                                    {jobPostingSavingId === posting.id ? (
                                      <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                      <Check className="w-3.5 h-3.5" />
                                    )}
                                    <span>保存</span>
                                  </button>
                                  <button
                                    onClick={() => handleCancelEdit(posting.id)}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors"
                                    title="キャンセル"
                                  >
                                    <X className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="px-6 py-4">
                                <span className="text-sm font-medium text-gray-900">
                                  {posting.title}
                                </span>
                              </td>
                              <td className="px-6 py-4 max-w-xs">
                                <p
                                  className="text-sm text-gray-500 truncate"
                                  title={posting.description || ''}
                                >
                                  {posting.description || <span className="text-gray-300">-</span>}
                                </p>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center justify-end gap-1">
                                  <button
                                    onClick={() => setEditingJobPostingId(posting.id)}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                                    title="編集"
                                  >
                                    <Pencil className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteJobPostingClick(posting.id)}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                    title="削除"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* ページネーション */}
                {totalPages > 1 && (
                  <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50/50">
                    <p className="text-sm text-gray-500">
                      全{jobPostings.length}件中{' '}
                      <span className="font-medium text-gray-700">
                        {(currentPage - 1) * ITEMS_PER_PAGE + 1}
                      </span>
                      〜
                      <span className="font-medium text-gray-700">
                        {Math.min(currentPage * ITEMS_PER_PAGE, jobPostings.length)}
                      </span>
                      件を表示
                    </p>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-white rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed border border-transparent hover:border-gray-200"
                      >
                        <ChevronLeft className="w-4 h-4" />
                        前へ
                      </button>
                      {getPageNumbers(currentPage, totalPages).map((page, index) =>
                        page === '...' ? (
                          <span key={`ellipsis-${index}`} className="px-2 text-gray-400 text-sm">
                            ...
                          </span>
                        ) : (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page as number)}
                            className={`inline-flex items-center justify-center w-8 h-8 text-sm rounded-lg transition-colors ${
                              page === currentPage
                                ? 'bg-indigo-600 text-white font-medium shadow-sm'
                                : 'text-gray-600 hover:bg-white hover:text-gray-900 border border-transparent hover:border-gray-200'
                            }`}
                          >
                            {page}
                          </button>
                        )
                      )}
                      <button
                        onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-white rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed border border-transparent hover:border-gray-200"
                      >
                        次へ
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* 職種設定セクション */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Briefcase className="w-5 h-5 text-purple-600" />
                      <h3 className="text-md font-semibold text-gray-900">職種設定</h3>
                    </div>
                    {loadingJobTypes && (
                      <div className="w-4 h-4 border-2 border-gray-300 border-t-purple-600 rounded-full animate-spin" />
                    )}
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    面接設定時に選択可能な職種をチェックしてください。
                    未選択の場合は全ての職種が表示されます。
                  </p>
                </div>
                <div className="p-6 space-y-4">
                  {allJobTypes.length === 0 && !loadingJobTypes && (
                    <p className="text-sm text-gray-500">利用可能な職種がありません</p>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {allJobTypes.map((jobType) => (
                      <label
                        key={jobType.id}
                        className="flex items-center space-x-3 p-2 rounded-md hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedJobTypeIds.has(jobType.id)}
                          onChange={() => handleJobTypeToggle(jobType.id)}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                        <span className="text-sm text-gray-700">{jobType.name}</span>
                      </label>
                    ))}
                  </div>
                  <div className="flex justify-end">
                    <button
                      onClick={handleSaveJobTypes}
                      disabled={isSaving}
                      className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                      {isSaving ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>保存中...</span>
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          <span>職種設定を保存</span>
                        </>
                      )}
                    </button>
                  </div>
                  <div className="text-xs text-gray-500 space-y-1">
                    <p>選択した職種は面接設定画面の「応募求人」ドロップダウンに表示されます。</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ユーザー管理タブ */}
          {activeTab === 'users' && (
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <Users className="w-5 h-5 text-blue-600" />
                <h2 className="text-lg font-semibold text-gray-900">ユーザー管理</h2>
              </div>
              <UserManagement companyId={companyId} />
            </div>
          )}

          {/* 利用状況タブ */}
          {activeTab === 'usage' && <UsageStatsTab />}
        </div>
      </div>

      {/* 削除確認ダイアログ */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">応募求人の削除確認</h3>
            <p className="text-gray-700 mb-6">
              この応募求人を削除してもよろしいですか？
              <br />
              <span className="text-sm text-orange-600 mt-2 block">
                使用中の場合は、関連する面接の応募求人情報が空欄になります。
              </span>
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleDeleteJobPostingCancel}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                キャンセル
              </button>
              <button
                onClick={handleDeleteJobPostingConfirm}
                disabled={jobPostingSavingId !== null}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {jobPostingSavingId ? '削除中...' : '削除'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanySettings;
